
import React, { useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { InstalacaoConfigForm } from "@/components/solar/configuracao/InstalacaoConfigForm";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation } from "@tanstack/react-query";

export type InstalacaoFormData = {
  concessionarias: string[];
  tiposTensao: { value: string; label: string }[];
  tipologias: { value: string; label: string }[];
  tiposTarifa: { value: string; label: string; tipologia: string }[];
  estruturas: { tipo: string; nome: string }[];
  difusaoAzimutal: number;
  inclinacaoTelhado: number;
  etapasObrigatorias: {
    selecionarConcessionaria: boolean;
    informarTipoTensao: boolean;
    selecionarTipologia: boolean;
    selecionarTipoTarifa: boolean;
    informarDifusaoAzimutal: boolean;
    informarInclinacaoTelhado: boolean;
    selecionarTipoTelhado: boolean;
  };
};

export const InstalacaoTab = () => {
  const methods = useForm<InstalacaoFormData>({
    defaultValues: {
      concessionarias: [],
      tiposTensao: [
        { value: "baixa", label: "Baixa Tensão" },
        { value: "media", label: "Média Tensão (A4)" }
      ],
      tipologias: [
        { value: "residencial", label: "Residencial" },
        { value: "comercial", label: "Comercial" },
        { value: "industrial", label: "Industrial" },
        { value: "rural", label: "Rural" }
      ],
      tiposTarifa: [],
      estruturas: [],
      difusaoAzimutal: 0,
      inclinacaoTelhado: 15,
      etapasObrigatorias: {
        selecionarConcessionaria: true,
        informarTipoTensao: true,
        selecionarTipologia: true,
        selecionarTipoTarifa: true,
        informarDifusaoAzimutal: false,
        informarInclinacaoTelhado: false,
        selecionarTipoTelhado: false
      }
    }
  });

  // Fetch saved configuration from Supabase
  const { data: dbConfig, isLoading } = useQuery({
    queryKey: ['instalacaoConfig'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('configuracoes_sistemas')
        .select('estruturas, valor_por_kwp')
        .order('created_at', { ascending: false })
        .limit(1);
        
      if (error) {
        console.error('Error fetching installation config:', error);
        return null;
      }
      
      return data?.[0] || null;
    }
  });

  // Mutation to save configuration to Supabase
  const { mutate: saveToSupabase } = useMutation({
    mutationFn: async (data: InstalacaoFormData) => {
      const { error } = await supabase
        .from('configuracoes_sistemas')
        .upsert({
          estruturas: data.estruturas,
          nome: 'Configuração de Instalação',
          valor_por_kwp: 4500, // Default value, can be updated later
          fator_capacidade: 0.8,
          fator_simultaneidade: 0.85,
          perdas: 0.15,
          eficiencia: 0.85
        });
      
      if (error) throw error;
      return data;
    },
    onError: (error) => {
      console.error('Error saving to Supabase:', error);
      toast.error("Erro ao salvar no banco de dados");
    }
  });

  // Load data from localStorage or database
  useEffect(() => {
    if (!isLoading && dbConfig) {
      try {
        // If we have estruturas from DB, use them
        if (dbConfig.estruturas && Array.isArray(dbConfig.estruturas)) {
          // Ensure the data has the correct shape before setting it
          const typedEstruturas = (dbConfig.estruturas as any[]).map(est => {
            if (typeof est === 'object' && est !== null) {
              return {
                tipo: est.tipo || '',
                nome: est.nome || ''
              };
            }
            return { tipo: '', nome: '' };
          });
          
          methods.setValue('estruturas', typedEstruturas);
        }
      } catch (error) {
        console.error("Erro ao carregar configurações da base de dados:", error);
      }
    }

    // Load from localStorage as fallback or for backward compatibility
    try {
      const savedData = localStorage.getItem("instalacaoConfigData");
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        methods.reset(parsedData);
      }
    } catch (error) {
      console.error("Erro ao carregar configurações do localStorage:", error);
    }
  }, [dbConfig, isLoading, methods]);

  const onSubmit = (data: InstalacaoFormData) => {
    try {
      // Save to localStorage for backward compatibility
      localStorage.setItem("instalacaoConfigData", JSON.stringify(data));
      
      // Save to Supabase
      saveToSupabase(data);
      
      toast.success("Configurações de instalação salvas com sucesso!");
    } catch (error) {
      console.error("Erro ao salvar configurações de instalação:", error);
      toast.error("Erro ao salvar configurações");
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <InstalacaoConfigForm />
      </form>
    </FormProvider>
  );
};
